<template>
    <div
        v-if="open"
        class="fixed inset-0 z-[2147483647] flex items-center justify-center bg-black/50 px-4 py-6"
        @click="handleBackdropClick"
    >
        <div class="relative w-full max-w-lg rounded-lg bg-white p-6 shadow-2xl dark:bg-gray-800" @click.stop>
            <button
                v-if="!isLocked"
                type="button"
                class="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-md text-gray-500 hover:text-gray-700 focus:outline-none dark:text-gray-400 dark:hover:text-gray-200"
                @click="handleCloseClick"
            >
                <span class="sr-only">Close</span>
                <i class="fa-solid fa-xmark text-lg"></i>
            </button>

            <div :class="isLocked ? '' : 'pr-10'">
                <h3 class="text-lg font-semibold text-gray-800 dark:text-gray-100">
                    {{ title }}
                </h3>
                <p v-if="meta" class="mt-1 text-xs text-gray-400">
                    {{ meta }}
                </p>
            </div>

            <div v-if="videoUrl" class="mt-4">
                <div class="relative overflow-hidden rounded-md bg-black">
                    <video
                        ref="video"
                        :key="identifier"
                        :src="videoUrl"
                        :poster="videoPoster || null"
                        :controls="!isLocked"
                        controlsList="nodownload"
                        playsinline
                        preload="metadata"
                        class="w-full"
                        @loadedmetadata="handleLoadedMetadata"
                        @timeupdate="handleTimeUpdate"
                        @seeking="handleSeeking"
                        @play="videoPlaying = true"
                        @pause="handlePause"
                        @ended="handleEnded"
                    ></video>

                    <button
                        v-if="isLocked && !videoPlaying"
                        type="button"
                        class="absolute inset-0 flex items-center justify-center bg-black/40 text-white transition-colors hover:bg-black/50"
                        @click="playVideo"
                    >
                        <span class="sr-only">Play video</span>
                        <i class="fa-solid fa-circle-play text-5xl"></i>
                    </button>
                </div>

                <div v-if="isLocked" class="mt-3">
                    <div class="flex items-center gap-3">
                        <button
                            type="button"
                            class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-100"
                            @click="togglePlayback"
                        >
                            <span class="sr-only">{{ videoPlaying ? 'Pause' : 'Play' }}</span>
                            <i :class="videoPlaying ? 'fa-solid fa-pause' : 'fa-solid fa-play'" class="text-xs"></i>
                        </button>

                        <div class="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-600">
                            <div
                                class="h-full rounded-full bg-midnight-500 transition-[width] duration-300"
                                :style="{ width: watchedPercent + '%' }"
                            ></div>
                        </div>

                        <span class="w-12 shrink-0 text-right text-xs tabular-nums text-gray-500">
                            {{ watchedPercent }}%
                        </span>
                    </div>

                    <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        Please watch this video all the way through to continue. It cannot be skipped.
                    </p>
                </div>
            </div>

            <p class="mt-4 whitespace-pre-line text-sm text-gray-700 dark:text-gray-200">{{ description }}</p>

            <div class="mt-5 flex flex-col gap-3">
                <a
                    v-if="ctaLabel && ctaUrl"
                    :href="ctaUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex w-full items-center justify-center rounded-md bg-midnight-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-midnight-400"
                    @click="handleCtaClick"
                >
                    {{ ctaLabel }}
                </a>

                <button
                    v-if="mode === 'popup'"
                    type="button"
                    :disabled="isLocked"
                    class="inline-flex w-full items-center justify-center rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
                    @click="handleDismissClick"
                >
                    {{ isLocked ? 'Watch the video to continue' : 'Dismiss' }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
// How often, in seconds of newly watched footage, progress is checkpointed to
// the server. Small enough that a closed tab loses very little, large enough
// not to chatter.
const PROGRESS_REPORT_INTERVAL = 5;

// Seeking further ahead than this many seconds past the furthest point actually
// watched is treated as a skip and rewound.
const SEEK_TOLERANCE = 1.5;

export default {
    data() {
        return {
            open: false,
            mode: 'read',
            queue: [],
            current: null,
            busy: false,
            maxWatchedSeconds: 0,
            videoDuration: 0,
            videoCompleted: false,
            videoPlaying: false,
            reportedSeconds: 0,
            correctingSeek: false,
        };
    },
    computed: {
        title() {
            return this.current?.title || 'Notification';
        },
        meta() {
            return this.current?.created_at_human || '';
        },
        description() {
            return this.current?.description || 'No description available.';
        },
        ctaLabel() {
            return (this.current?.cta_label || '').trim();
        },
        ctaUrl() {
            return (this.current?.cta_url || '').trim();
        },
        identifier() {
            return this.current?.reference || this.current?.id || null;
        },
        videoUrl() {
            return (this.current?.video_url || '').trim();
        },
        videoPoster() {
            return (this.current?.video_thumbnail_url || '').trim();
        },
        /**
         * Locked means the user must watch the video before they can get past
         * this announcement: no close button, no backdrop click, no Escape and
         * no dismiss. Only applies to pop-ups — reading an announcement from
         * the dropdown dismisses nothing, so there is nothing to gate.
         */
        isLocked() {
            return (
                this.mode === 'popup' &&
                !!this.videoUrl &&
                !!this.current?.requires_video_completion &&
                !this.videoCompleted
            );
        },
        watchedPercent() {
            if (!this.videoDuration) return 0;
            return Math.min(100, Math.round((this.maxWatchedSeconds / this.videoDuration) * 100));
        },
    },
    mounted() {
        document.addEventListener('keydown', this.handleKeydown);
        window.addEventListener('beforeunload', this.flushProgress);
    },
    beforeUnmount() {
        document.removeEventListener('keydown', this.handleKeydown);
        window.removeEventListener('beforeunload', this.flushProgress);
    },
    methods: {
        openNotification(notification, options = {}) {
            this.mode = options.mode === 'popup' ? 'popup' : 'read';
            this.queue = [];
            this.setCurrent(notification || null);
        },
        openQueue(notifications = []) {
            this.mode = 'popup';
            this.queue = Array.isArray(notifications) ? [...notifications] : [];
            this.setCurrent(this.queue.shift() || null);
        },
        setCurrent(notification) {
            this.current = notification;
            this.maxWatchedSeconds = Number(notification?.video_watched_seconds) || 0;
            this.reportedSeconds = this.maxWatchedSeconds;
            this.videoCompleted = !!notification?.video_completed;
            this.videoDuration = 0;
            this.videoPlaying = false;
            this.open = !!this.current;

            if (this.open) {
                document.body.style.overflow = 'hidden';
            }
        },

        // --- Video playback -------------------------------------------------

        playVideo() {
            const video = this.$refs.video;
            if (video) video.play();
        },
        togglePlayback() {
            const video = this.$refs.video;
            if (!video) return;
            if (video.paused) {
                video.play();
            } else {
                video.pause();
            }
        },
        handleLoadedMetadata(event) {
            const duration = event.target.duration;
            this.videoDuration = Number.isFinite(duration) ? Math.floor(duration) : 0;

            // Pick up where they left off rather than making them start again.
            if (this.maxWatchedSeconds > 0 && this.maxWatchedSeconds < this.videoDuration) {
                event.target.currentTime = this.maxWatchedSeconds;
            }
        },
        handleTimeUpdate(event) {
            const currentTime = event.target.currentTime;

            // Only count time reached by playing through; a jump forward past
            // the furthest watched point contributes nothing.
            if (currentTime - this.maxWatchedSeconds <= SEEK_TOLERANCE + 1) {
                this.maxWatchedSeconds = Math.max(this.maxWatchedSeconds, Math.floor(currentTime));
            }

            if (this.maxWatchedSeconds - this.reportedSeconds >= PROGRESS_REPORT_INTERVAL) {
                this.reportProgress();
            }
        },
        handleSeeking(event) {
            if (!this.isLocked || this.correctingSeek) return;

            const video = event.target;
            if (video.currentTime > this.maxWatchedSeconds + SEEK_TOLERANCE) {
                this.correctingSeek = true;
                video.currentTime = this.maxWatchedSeconds;
                this.$nextTick(() => {
                    this.correctingSeek = false;
                });
            }
        },
        handlePause() {
            this.videoPlaying = false;
            this.reportProgress();
        },
        async handleEnded() {
            this.videoPlaying = false;
            if (this.videoDuration) {
                this.maxWatchedSeconds = this.videoDuration;
            }
            await this.reportProgress(true);
        },
        async reportProgress(force = false) {
            if (!this.videoUrl || !this.identifier) return;
            if (!force && this.maxWatchedSeconds <= this.reportedSeconds) return;

            const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
            if (!token) return;

            const watched = this.maxWatchedSeconds;
            this.reportedSeconds = watched;

            try {
                const response = await fetch(`/notifications/${this.identifier}/video-progress`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': token,
                    },
                    body: JSON.stringify({
                        watched_seconds: watched,
                        duration_seconds: this.videoDuration || null,
                    }),
                });

                if (!response.ok) return;

                const data = await response.json();
                // The server is the authority on completion, not the player.
                if (data?.completed) {
                    this.videoCompleted = true;
                }
                if (Number.isFinite(data?.max_watched_seconds)) {
                    this.maxWatchedSeconds = Math.max(this.maxWatchedSeconds, data.max_watched_seconds);
                }
            } catch (e) {
                // A dropped checkpoint is not fatal; the next one will carry it.
                this.reportedSeconds = Math.max(0, watched - PROGRESS_REPORT_INTERVAL);
            }
        },
        flushProgress() {
            if (!this.videoUrl || !this.identifier) return;
            if (this.maxWatchedSeconds <= this.reportedSeconds) return;

            const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
            if (!token || !navigator.sendBeacon) return;

            const payload = new FormData();
            payload.append('watched_seconds', String(this.maxWatchedSeconds));
            if (this.videoDuration) payload.append('duration_seconds', String(this.videoDuration));
            payload.append('_token', token);

            navigator.sendBeacon(`/notifications/${this.identifier}/video-progress`, payload);
        },

        // --- Dismissal ------------------------------------------------------

        async handleCloseClick() {
            if (this.busy || this.isLocked) return;
            if (this.mode === 'popup') {
                if (await this.dismissCurrent()) {
                    this.nextFromQueue();
                }
                return;
            }
            await this.markAsReadCurrent();
            this.finish();
        },
        async handleDismissClick() {
            if (this.busy || this.isLocked) return;
            if (await this.dismissCurrent()) {
                this.nextFromQueue();
            }
        },
        handleBackdropClick() {
            this.handleCloseClick();
        },
        handleKeydown(e) {
            if (!this.open) return;
            if (e.key === 'Escape') {
                this.handleCloseClick();
            }
        },
        handleCtaClick() {
            if (this.isLocked) return;
            if (this.mode === 'popup') {
                this.handleDismissClick();
                return;
            }
            this.markAsReadCurrent();
            this.finish();
        },
        async markAsReadCurrent() {
            const identifier = this.identifier;
            if (!identifier) return;
            const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
            if (!token) return;
            this.busy = true;
            try {
                await fetch(`/notifications/${identifier}/read`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': token,
                    },
                });
                this.updateReadUi();
            } finally {
                this.busy = false;
            }
        },
        async dismissCurrent() {
            const identifier = this.identifier;
            if (!identifier) return false;
            const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
            if (!token) return false;
            this.busy = true;
            try {
                const response = await fetch(`/notifications/${identifier}/dismiss`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': token,
                    },
                });

                // The server refuses to dismiss a gated announcement that has
                // not been watched; re-lock the modal rather than closing it.
                if (response.status === 422) {
                    this.videoCompleted = false;
                    return false;
                }

                this.updateReadUi();
                return true;
            } finally {
                this.busy = false;
            }
        },
        updateReadUi() {
            const notificationId = this.current?.id;
            if (!notificationId) return;

            const item = document.querySelector(`.notification-item[data-id="${notificationId}"]`);
            if (item) {
                item.classList.remove('bg-gray-100');
                item.classList.add('bg-white');
            }

            document.querySelectorAll('.profile-nav-icon-badge').forEach((badge) => {
                const count = parseInt((badge.textContent || '').trim(), 10);
                if (Number.isFinite(count) && count > 0) {
                    const next = count - 1;
                    badge.textContent = String(next);
                    if (next === 0) {
                        badge.classList.add('hidden');
                    }
                }
            });
        },
        nextFromQueue() {
            const next = this.queue.shift() || null;
            if (next) {
                this.setCurrent(next);
                return;
            }
            this.finish();
        },
        finish() {
            this.open = false;
            this.current = null;
            this.queue = [];
            this.videoPlaying = false;
            document.body.style.overflow = '';
        },
    },
};
</script>
